// src/utils/offline.js

let db = null

const DB_NAME = 'treasureHuntDB'
const DB_VERSION = 1
const STORES = {
  HUNT: 'hunt',
  PROGRESS: 'progress',
  SYNC_QUEUE: 'syncQueue'
}

/**
 * Initialize IndexedDB for offline storage
 * Creates object stores if they don't exist
 */
export async function initOfflineDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onerror = () => reject(request.error)
    request.onsuccess = () => {
      db = request.result
      resolve(db)
    }

    request.onupgradeneeded = (event) => {
      const database = event.target.result

      // Hunt cache store
      if (!database.objectStoreNames.contains(STORES.HUNT)) {
        database.createObjectStore(STORES.HUNT, { keyPath: 'id' })
      }

      // Progress store (single document with id='current')
      if (!database.objectStoreNames.contains(STORES.PROGRESS)) {
        database.createObjectStore(STORES.PROGRESS, { keyPath: 'id' })
      }

      // Sync queue for offline changes
      if (!database.objectStoreNames.contains(STORES.SYNC_QUEUE)) {
        const syncStore = database.createObjectStore(STORES.SYNC_QUEUE, { autoIncrement: true })
        syncStore.createIndex('timestamp', 'timestamp', { unique: false })
      }
    }
  })
}

/**
 * Get hunt progress from IndexedDB
 * Returns default structure if not found
 */
export async function getProgress() {
  return new Promise((resolve) => {
    if (!db) {
      resolve({
        foundTreasures: [],
        unscrambledWords: [],
        puzzleCompleted: false
      })
      return
    }

    const transaction = db.transaction([STORES.PROGRESS], 'readonly')
    const store = transaction.objectStore(STORES.PROGRESS)
    const request = store.get('current')

    request.onsuccess = () => {
      const result = request.result || {
        id: 'current',
        foundTreasures: [],
        unscrambledWords: [],
        puzzleCompleted: false
      }
      resolve(result)
    }

    request.onerror = () => {
      resolve({
        foundTreasures: [],
        unscrambledWords: [],
        puzzleCompleted: false
      })
    }
  })
}

/**
 * Save hunt progress to IndexedDB
 */
export async function saveProgress(progress) {
  return new Promise((resolve) => {
    if (!db) {
      resolve(false)
      return
    }

    const transaction = db.transaction([STORES.PROGRESS], 'readwrite')
    const store = transaction.objectStore(STORES.PROGRESS)
    const request = store.put({
      id: 'current',
      ...progress,
      updatedAt: Date.now()
    })

    request.onsuccess = () => resolve(true)
    request.onerror = () => resolve(false)
  })
}

/**
 * Cache hunt data in IndexedDB
 */
export async function saveHuntCache(hunt) {
  return new Promise((resolve) => {
    if (!db) {
      resolve(false)
      return
    }

    const transaction = db.transaction([STORES.HUNT], 'readwrite')
    const store = transaction.objectStore(STORES.HUNT)
    const request = store.put({
      ...hunt,
      cachedAt: Date.now()
    })

    request.onsuccess = () => resolve(true)
    request.onerror = () => resolve(false)
  })
}

/**
 * Get cached hunt from IndexedDB
 */
export async function getHuntCache() {
  return new Promise((resolve) => {
    if (!db) {
      resolve(null)
      return
    }

    const transaction = db.transaction([STORES.HUNT], 'readonly')
    const store = transaction.objectStore(STORES.HUNT)
    const request = store.getAll()

    request.onsuccess = () => {
      const result = request.result
      resolve(result.length > 0 ? result[0] : null)
    }

    request.onerror = () => resolve(null)
  })
}

/**
 * Add action to sync queue (for offline changes)
 */
export async function addToSyncQueue(action) {
  return new Promise((resolve) => {
    if (!db) {
      resolve(false)
      return
    }

    const transaction = db.transaction([STORES.SYNC_QUEUE], 'readwrite')
    const store = transaction.objectStore(STORES.SYNC_QUEUE)
    const request = store.add({
      ...action,
      timestamp: Date.now()
    })

    request.onsuccess = () => resolve(true)
    request.onerror = () => resolve(false)
  })
}

/**
 * Get all pending sync actions
 */
export async function getSyncQueue() {
  return new Promise((resolve) => {
    if (!db) {
      resolve([])
      return
    }

    const transaction = db.transaction([STORES.SYNC_QUEUE], 'readonly')
    const store = transaction.objectStore(STORES.SYNC_QUEUE)
    const request = store.getAll()

    request.onsuccess = () => resolve(request.result)
    request.onerror = () => resolve([])
  })
}

/**
 * Clear all pending sync actions after successful sync
 */
export async function clearSyncQueue() {
  return new Promise((resolve) => {
    if (!db) {
      resolve(false)
      return
    }

    const transaction = db.transaction([STORES.SYNC_QUEUE], 'readwrite')
    const store = transaction.objectStore(STORES.SYNC_QUEUE)
    const request = store.clear()

    request.onsuccess = () => resolve(true)
    request.onerror = () => resolve(false)
  })
}

/**
 * Register Service Worker for offline support
 */
export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) {
    console.warn('Service Workers not supported')
    return null
  }

  try {
    const registration = await navigator.serviceWorker.register('/sw.js')
    console.log('Service Worker registered:', registration)
    return registration
  } catch (error) {
    console.error('Service Worker registration failed:', error)
    return null
  }
}
